import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { format } from 'date-fns';
import { BRAND } from '../../theme';
import { CompletionState } from './sections';

interface WorkspaceHeaderProps {
  rowNumber: string;
  broker: string;
  brokers: string[];
  onBrokerChange: (broker: string) => void;
  invoiceSheet: string;
  invoiceSheets: string[];
  onInvoiceSheetChange: (sheet: string) => void;
  documentPage: number | null;
  documentPageCount: number | null;
  autoSaveEnabled: boolean;
  onToggleAutoSave: () => void;
  draftSavedAt: number | null;
  completion: CompletionState;
}

/** One labelled readout in the status strip. */
const StatusTile: React.FC<{
  label: string;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  title?: string;
  /** Hide on phones, where the strip only keeps what's worth the space */
  desktopOnly?: boolean;
}> = ({ label, children, onClick, title, desktopOnly }) => (
  <Tooltip title={title || ''}>
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        display: desktopOnly ? { xs: 'none', md: 'block' } : 'block',
        flexShrink: 0,
        px: { xs: 1.5, md: 1.75 },
        py: { xs: 0.75, md: 1 },
        minWidth: { xs: 0, md: 104 },
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: BRAND.blue } : undefined
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.4 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{children}</Box>
    </Paper>
  </Tooltip>
);

const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  rowNumber,
  broker,
  brokers,
  onBrokerChange,
  invoiceSheet,
  invoiceSheets,
  onInvoiceSheetChange,
  documentPage,
  documentPageCount,
  autoSaveEnabled,
  onToggleAutoSave,
  draftSavedAt,
  completion
}) => {
  const [brokerAnchor, setBrokerAnchor] = useState<null | HTMLElement>(null);
  const [sheetAnchor, setSheetAnchor] = useState<null | HTMLElement>(null);

  const documentLabel =
    documentPageCount && documentPage
      ? `Page ${documentPage} of ${documentPageCount}`
      : 'No PDF loaded';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'stretch', lg: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1.25, md: 2 },
        px: { xs: 1.5, md: 3 },
        py: { xs: 1.5, md: 2 }
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
          Invoice Data Entry Workspace
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: { xs: 'none', md: 'block' } }}
        >
          Capture invoice information faster with side-by-side document review.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          // Phones: one swipeable row rather than a wall of wrapped tiles.
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          overflowX: { xs: 'auto', md: 'visible' },
          mx: { xs: -1.5, md: 0 },
          px: { xs: 1.5, md: 0 },
          pb: { xs: 0.25, md: 0 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          alignItems: 'center',
          gap: { xs: 1, md: 1.5 }
        }}
      >
        <StatusTile label="Current Invoice">
          <Typography variant="subtitle2">Row {rowNumber || '—'}</Typography>
        </StatusTile>

        <StatusTile
          label="Broker"
          title="Change broker"
          onClick={(e) => setBrokerAnchor(e.currentTarget)}
        >
          <Typography variant="subtitle2" noWrap>
            {broker || 'Select'}
          </Typography>
          <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </StatusTile>

        <StatusTile
          label="Invoice Sheet"
          title="Change invoice sheet"
          onClick={(e) => setSheetAnchor(e.currentTarget)}
        >
          <Typography variant="subtitle2" noWrap>
            {invoiceSheet}
          </Typography>
          <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </StatusTile>

        <StatusTile label="Document" desktopOnly>
          <Typography variant="subtitle2" noWrap>
            {documentLabel}
          </Typography>
        </StatusTile>

        <StatusTile
          label="Auto-save"
          title={
            draftSavedAt
              ? `Draft saved at ${format(new Date(draftSavedAt), 'h:mm:ss a')}`
              : 'Drafts are kept in this browser'
          }
          onClick={onToggleAutoSave}
        >
          <Chip
            size="small"
            label={autoSaveEnabled ? 'ON' : 'OFF'}
            sx={{
              height: 20,
              backgroundColor: autoSaveEnabled ? '#DCFCE7' : '#F1F5F9',
              color: autoSaveEnabled ? '#166534' : BRAND.textMuted
            }}
          />
        </StatusTile>

        <Paper
          variant="outlined"
          sx={{
            px: { xs: 1.5, md: 1.75 },
            py: { xs: 0.75, md: 1 },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={38}
              thickness={4}
              sx={{ color: BRAND.border }}
            />
            <CircularProgress
              variant="determinate"
              value={completion.percent}
              size={38}
              thickness={4}
              sx={{ color: BRAND.blue, position: 'absolute', left: 0 }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center'
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>
                {completion.percent}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Fields complete
            </Typography>
            <Typography variant="subtitle2">
              {completion.completed} / {completion.total}
            </Typography>
          </Box>
        </Paper>

        <Tooltip title="The form and the document scroll separately, so you can keep a field in view while paging through the PDF.">
          <Paper
            variant="outlined"
            sx={{
              px: 1.75,
              py: 1.25,
              // Phones scroll the page as one; there's no split view to explain.
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              backgroundColor: '#EFF4FE',
              borderColor: '#C7DBFA'
            }}
          >
            <SwapHorizIcon fontSize="small" sx={{ color: BRAND.blue }} />
            <Typography variant="body2" sx={{ color: BRAND.navy, fontWeight: 600 }}>
              Independent scrolling enabled
            </Typography>
            <InfoOutlinedIcon fontSize="small" sx={{ color: BRAND.blue }} />
          </Paper>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={brokerAnchor}
        open={Boolean(brokerAnchor)}
        onClose={() => setBrokerAnchor(null)}
      >
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
          Broker
        </Typography>
        <Divider sx={{ mt: 0.5 }} />
        {brokers.length === 0 && <MenuItem disabled>No brokers found</MenuItem>}
        {brokers.map((option) => (
          <MenuItem
            key={option}
            selected={option === broker}
            onClick={() => {
              onBrokerChange(option);
              setBrokerAnchor(null);
            }}
          >
            <ListItemText>{option}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={sheetAnchor}
        open={Boolean(sheetAnchor)}
        onClose={() => setSheetAnchor(null)}
      >
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
          Invoice sheet
        </Typography>
        <Divider sx={{ mt: 0.5 }} />
        {invoiceSheets.map((option) => (
          <MenuItem
            key={option}
            selected={option === invoiceSheet}
            onClick={() => {
              onInvoiceSheetChange(option);
              setSheetAnchor(null);
            }}
          >
            <ListItemText>{option}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default WorkspaceHeader;
